import { Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../../../components/CopyableId';
import { Blank, EntryTypeBadge, Money, OrgCell, WhenCell, entryTypeOf } from '../../components/cells';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';

type Entry = NonNullable<Awaited<ReturnType<typeof admin.listLedger>>['data']>[number];

/** The two columns the journal can be ordered by, shared with the page's sort control. */
export type LedgerSort = 'occurred_at:desc' | 'occurred_at:asc' | 'amount_minor:desc' | 'amount_minor:asc';

export function LedgerTable({ rows, loading, sort, onSortChange }: {
  rows: Entry[];
  loading: boolean;
  sort: LedgerSort;
  onSortChange: (next: LedgerSort) => void;
}) {
  const t = useT();
  const [sortKey, sortDirection] = sort.split(':') as ['occurred_at' | 'amount_minor', 'asc' | 'desc'];

  return (
    <DataTable<Indexed<Entry>>
      records={rows as Indexed<Entry>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('billing.ledger.emptyRows')}
      // The page sorts the whole filtered set and hands over one page of it, so
      // the header arrows have to drive that sort rather than reordering the
      // twenty-five rows that happen to be visible.
      sortStatus={{ columnAccessor: sortKey, direction: sortDirection }}
      onSortStatusChange={(next) => {
        if (next.columnAccessor === 'occurred_at' || next.columnAccessor === 'amount_minor') {
          onSortChange(`${next.columnAccessor}:${next.direction}` as LedgerSort);
        }
      }}
      columns={[
        {
          accessor: 'occurred_at',
          title: t('billing.ledger.columns.createdAt'),
          sortable: true,
          render: (r) => <WhenCell iso={r.occurred_at} />
        },
        {
          accessor: 'organisation_id',
          title: t('billing.ledger.columns.organisation'),
          render: (r) => <OrgCell id={r.organisation_id} />
        },
        {
          accessor: 'entry_type',
          title: t('billing.ledger.columns.entryType'),
          render: (r) => <EntryTypeBadge type={entryTypeOf(r)} />
        },
        {
          accessor: 'sku_code',
          title: t('billing.ledger.columns.skuCode'),
          render: (r) => r.sku_code ? <code>{r.sku_code}</code> : <Blank />
        },
        {
          accessor: 'description',
          title: t('billing.ledger.columns.description'),
          render: (r) => r.description
            ? <Text role="bodySmall" lineClamp={1} title={r.description}>{r.description}</Text>
            : <Blank />
        },
        {
          accessor: 'amount_minor',
          title: t('common.amount'),
          textAlign: 'right',
          // Sortable because "show me the largest debits" is the second question
          // an operator asks of a journal, after "what happened when".
          sortable: true,
          render: (r) => <Money minor={r.amount_minor} currency={r.currency} signed />
        },
        {
          accessor: 'currency',
          title: t('common.currency'),
          render: (r) => <Text role="bodySmall" c="var(--rr-color-text-muted)">{r.currency}</Text>
        },
        {
          accessor: 'idempotency_key',
          title: t('billing.ledger.columns.reference'),
          // The reference and the entry id are the two values a person quotes
          // in a support thread when reconciling, so both are copyable rather
          // than decorative.
          render: (r) => r.idempotency_key
            ? <CopyableId value={r.idempotency_key} label={t('billing.ledger.copyReference')} chars={12} />
            : <Blank />
        },
        {
          accessor: 'id',
          title: t('billing.ledger.columns.entryId'),
          render: (r) => <CopyableId value={r.id} label={t('billing.ledger.copyEntryId')} chars={12} />
        }
      ]}
    />
  );
}
